import { z } from "zod";

/**
 * KYC wizard shared contract (docs/specs/kyc-aml-profile.md, PR 4).
 *
 * Single source of truth for the `/kyc/me*` wire shapes used by the web
 * wizard and the NestJS slice (`apps/api/src/kyc/`). apps/api does not import
 * this package at runtime — `apps/api/src/kyc/dto/kyc.dto.ts` mirrors these
 * schemas field for field.
 */

export const KYC_CAPACITIES = ["self", "entity_rep", "poa_agent"] as const;
export const KYC_PURPOSES = ["sell", "buy", "lease_out", "rent", "manage", "services"] as const;
export const KYC_VALUE_BANDS = ["lt_100k", "100k_500k", "500k_2m", "2m_5m", "gt_5m"] as const;
export const KYC_EMPLOYMENT = ["employed", "self_employed", "business_owner", "retired", "student", "not_working"] as const;
export const KYC_FUND_SOURCES = ["salary", "business_income", "savings", "inheritance", "bank_financing", "other"] as const;
export const KYC_ENTITY_POSITIONS = ["manager", "partner", "board_member", "authorised_employee", "agent"] as const;
export const KYC_OWNERSHIP_STRUCTURES = ["individuals", "companies", "listed", "government"] as const;
export const KYC_CONTROL = ["shares_25", "votes_25", "other_control", "senior_official"] as const;
export const KYC_PEP_RELATIONS = ["self", "family", "associate"] as const;
export const KYC_DOCUMENT_KINDS = [
  "passport",
  "selfie",
  "poa",
  "authorisation",
  "registration",
  "commercial_registration",
  "iban_letter",
  "supporting",
] as const;
export const KYC_SECTIONS = ["passport", "capacity", "about", "company", "ownership", "poa", "principal"] as const;
export const KYC_DECLARATION_KEYS = ["accurate", "lawful_funds", "update_within_30_days", "privacy_aml"] as const;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const iso2 = z.string().regex(/^[A-Z]{2}$/);
const text = (max = 200) => z.string().trim().min(1).max(max);
const saudiIdNumber = z.string().regex(/^[12]\d{9}$/);
const unifiedNumber = z.string().regex(/^7\d{9}$/);

export const kycPepSchema = z
  .object({
    isPep: z.boolean(),
    relation: z.enum(KYC_PEP_RELATIONS).optional(),
    position: text().optional(),
    country: iso2.optional(),
    since: z.string().regex(/^\d{4}$/).optional(),
  })
  .refine((p) => !p.isPep || (p.relation && p.position && p.country), {
    message: "relation, position and country are required for a politically exposed person",
    path: ["relation"],
  });

export const kycAboutSchema = z
  .object({
    employment: z.enum(KYC_EMPLOYMENT),
    jobTitle: text().optional(),
    employer: text().optional(),
    purposes: z.array(z.enum(KYC_PURPOSES)).min(1),
    fundSources: z.array(z.enum(KYC_FUND_SOURCES)).min(1),
    otherFundSource: text().optional(),
    valueBand: z.enum(KYC_VALUE_BANDS),
    pep: kycPepSchema,
  })
  .refine((a) => !a.fundSources.includes("other") || !!a.otherFundSource, {
    message: "describe the other source of funds",
    path: ["otherFundSource"],
  });

export const kycCompanySchema = z
  .object({
    unifiedNumber,
    name: text(),
    legalForm: text(100),
    crExpiry: isoDate,
    city: text(100),
    position: z.enum(KYC_ENTITY_POSITIONS),
    vatNumber: z.string().regex(/^3\d{13}3$/).optional(),
    email: z.string().email().optional(),
    crDocId: z.string().uuid(),
    authorisationDocId: z.string().uuid().optional(),
  })
  .refine((c) => c.position === "manager" || !!c.authorisationDocId, {
    message: "an authorisation letter or board resolution is required unless you are a manager",
    path: ["authorisationDocId"],
  });

export const kycOwnerSchema = z.object({
  name: text(),
  idNumber: z.string().trim().regex(/^[0-9A-Za-z]{5,20}$/),
  nationality: iso2,
  pct: z.number().min(0).max(100),
  control: z.enum(KYC_CONTROL),
  isPep: z.boolean(),
});

export const kycOwnershipSchema = z
  .object({
    structure: z.enum(KYC_OWNERSHIP_STRUCTURES),
    owners: z.array(kycOwnerSchema).max(20),
  })
  .superRefine((o, ctx) => {
    if (o.structure === "listed" || o.structure === "government") return;
    if (o.owners.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "list at least one owner or controller", path: ["owners"] });
      return;
    }
    const total = o.owners.reduce((sum, w) => sum + w.pct, 0);
    const controlOtherwise = o.owners.some((w) => w.control === "other_control" || w.control === "senior_official");
    if (total > 100 || (!controlOtherwise && Math.abs(total - 100) > 0.001)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "ownership must total 100%", path: ["owners"] });
    }
  });

export const kycPoaSchema = z
  .object({
    principalType: z.enum(["person", "entity"]),
    principalIdNumber: z.string(),
    principalName: text(),
    poaNumber: text(50),
    expiresOn: isoDate,
    poaDocId: z.string().uuid(),
    coversProperty: z.literal(true),
    principalIsPep: z.boolean(),
  })
  .refine(
    (p) => (p.principalType === "person" ? saudiIdNumber : unifiedNumber).safeParse(p.principalIdNumber).success,
    { message: "the principal ID number is not valid", path: ["principalIdNumber"] },
  );

/** Whole years from a `YYYY-MM-DD` birth date to today. */
function ageOn(dob: string, today = new Date()): number {
  const [y, m, d] = dob.split("-").map(Number);
  let age = today.getUTCFullYear() - y;
  if (today.getUTCMonth() + 1 < m || (today.getUTCMonth() + 1 === m && today.getUTCDate() < d)) age--;
  return age;
}

/** S1b — identity from a passport when the person has no Saudi ID (always manually reviewed). */
export const kycPassportSchema = z
  .object({
    fullName: text(),
    passportNumber: z.string().trim().regex(/^[A-Z0-9]{5,20}$/),
    issuingCountry: iso2,
    nationality: iso2,
    dob: isoDate,
    expiresOn: isoDate,
    homeAddress: text(300),
    passportDocId: z.string().uuid(),
    selfieDocId: z.string().uuid(),
  })
  .refine((p) => ageOn(p.dob) >= 18, { message: "the account holder must be 18 or over", path: ["dob"] })
  .refine((p) => p.expiresOn > new Date().toISOString().slice(0, 10), { message: "the passport has expired", path: ["expiresOn"] });

/** Body of PUT /kyc/me/draft. */
export const saveKycSectionSchema = z.discriminatedUnion("section", [
  z.object({ section: z.literal("capacity"), value: z.enum(KYC_CAPACITIES) }),
  z.object({ section: z.literal("about"), value: kycAboutSchema }),
  z.object({ section: z.literal("company"), value: kycCompanySchema }),
  z.object({ section: z.literal("ownership"), value: kycOwnershipSchema }),
  z.object({ section: z.literal("poa"), value: kycPoaSchema }),
  z.object({ section: z.literal("principal"), value: kycAboutSchema }),
  z.object({ section: z.literal("passport"), value: kycPassportSchema }),
]);
export type SaveKycSection = z.infer<typeof saveKycSectionSchema>;

/** Body of POST /kyc/me/submit — every declaration ticked. */
export const submitKycSchema = z.object({
  declarations: z.object({
    accurate: z.literal(true),
    lawful_funds: z.literal(true),
    update_within_30_days: z.literal(true),
    privacy_aml: z.literal(true),
  }),
});

/** Body of PUT /kyc/me/payout-account. */
export const setPayoutAccountSchema = z.object({
  iban: z.string().trim().min(15).max(40),
  holderName: text(),
  letterDocId: z.string().uuid().optional(),
});

/** A payout account as the API returns it — masked IBAN, bank derived from its SAMA code. */
export const payoutAccountSchema = z.object({
  ibanLast4: z.string(),
  bankCode: z.string(),
  bankName: z.object({ ar: z.string(), en: z.string() }).nullable(),
  holderName: z.string(),
});
export type PayoutAccount = z.infer<typeof payoutAccountSchema>;

export const kycDraftSchema = z.object({
  capacity: z.enum(KYC_CAPACITIES).optional(),
  about: kycAboutSchema.optional(),
  company: kycCompanySchema.optional(),
  ownership: kycOwnershipSchema.optional(),
  poa: kycPoaSchema.optional(),
  principal: kycAboutSchema.optional(),
  passport: kycPassportSchema.optional(),
});
export type KycDraft = z.infer<typeof kycDraftSchema>;

/** Response of GET /kyc/me (and of every write that returns the profile). */
export const kycMeResponseSchema = z.object({
  status: z.enum(["in_progress", "under_review", "action_needed", "verified", "update_due"]),
  /** The activation lock: the customer portals stay shut until this is false. */
  portalLocked: z.boolean(),
  capacity: z.enum(KYC_CAPACITIES).nullable(),
  draft: kycDraftSchema,
  missing: z.array(z.enum(KYC_SECTIONS)),
  currentVersion: z.number().int(),
  submittedAt: z.string().nullable(),
  nextReviewAt: z.string().nullable(),
  documents: z.array(z.object({ id: z.string().uuid(), kind: z.enum(KYC_DOCUMENT_KINDS), uploadedAt: z.string() })),
  payoutAccount: payoutAccountSchema.nullable(),
});
export type KycMeResponse = z.infer<typeof kycMeResponseSchema>;
